import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/seo/seo';
import { Header, Footer } from '@/components/layout';
import { Home, Search } from 'lucide-react';

const NotFoundPage = () => {
  const { t } = useTranslation();

  return (
    <>
      {/* noindex tells Google not to index this page (standard SPA 404 handling when
          the host can't return a real HTTP 404 status for SPA routes). */}
      <Seo
        title="Page Not Found (404) | Quinta da Arrábida"
        description="The page you're looking for doesn't exist. Return to Quinta da Arrábida's homepage to explore houses, experiences and local guides."
        noindex
      />
      <Header />
      <main className="min-h-[70vh] flex items-center justify-center bg-stone-50 px-6 py-20">
        <div className="max-w-xl text-center" data-testid="404-page">
          <p className="text-7xl md:text-8xl font-serif text-emerald-800 mb-4">404</p>
          <h1 className="text-2xl md:text-3xl font-serif text-stone-800 mb-4">
            {t('notFound.title', 'Page not found')}
          </h1>
          <p className="text-base text-stone-600 mb-8 leading-relaxed">
            {t(
              'notFound.description',
              "The page you're looking for doesn't exist or has been moved. Let us help you find your way back."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors"
              data-testid="404-home-btn"
            >
              <Home size={16} />
              {t('notFound.home', 'Back to Homepage')}
            </Link>
            <Link
              to="/houses"
              className="inline-flex items-center justify-center gap-2 bg-white border border-stone-300 hover:border-emerald-800 text-stone-800 px-6 py-3 rounded-full text-sm font-medium transition-colors"
              data-testid="404-houses-btn"
            >
              <Search size={16} />
              {t('notFound.houses', 'Browse Houses')}
            </Link>
          </div>
          <div className="mt-12 pt-8 border-t border-stone-200">
            <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider">
              {t('notFound.popular', 'Popular pages')}
            </p>
            <div className="flex flex-wrap gap-2 justify-center text-sm">
              <Link to="/experiences" className="text-emerald-800 hover:underline">{t('notFound.linkExperiences', 'Experiences')}</Link>
              <span className="text-stone-400">·</span>
              <Link to="/guide/arrabida" className="text-emerald-800 hover:underline">{t('notFound.linkGuide', 'Arrábida Guide')}</Link>
              <span className="text-stone-400">·</span>
              <Link to="/blog" className="text-emerald-800 hover:underline">{t('notFound.linkBlog', 'Blog')}</Link>
              <span className="text-stone-400">·</span>
              <Link to="/book" className="text-emerald-800 hover:underline">{t('notFound.linkBook', 'Book a Stay')}</Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default NotFoundPage;
